import React, { useState, useEffect, useRef } from "react";
import {
  User,
  Menu,
  X,
  LogIn,
  LogOut,
  Ticket,
  Film,
  Calendar,
  Gift,
  Popcorn,
  ChevronsDown,
  Newspaper,
} from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const PremiumNavbar = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Check authentication status when component mounts
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Listen for storage events to handle login/logout in other tabs
  useEffect(() => {
    window.addEventListener("storage", checkAuthStatus);
    return () => window.removeEventListener("storage", checkAuthStatus);
  }, []);

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close menus when clicking outside
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
  //       setUserMenuOpen(false);
  //     }
  //     if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target) &&
  //         event.target.getAttribute('aria-label') !== 'Toggle mobile menu') {
  //       setMobileMenuOpen(false);
  //     }
  //   };

  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => document.removeEventListener('mousedown', handleClickOutside);
  // }, []);

  const checkAuthStatus = () => {
    try {
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");

      if (token && user) {
        const parsedUser = JSON.parse(user);
        setIsLoggedIn(true);
        setUserData(parsedUser);
      } else {
        setIsLoggedIn(false);
        setUserData({});
      }
    } catch (error) {
      console.error("Error checking authentication status:", error);
      // Handle corrupted localStorage data
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setIsLoggedIn(false);
      setUserData({});
    }
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const handleLogout = () => {
    // Show confirmation dialog
    if (window.confirm("Are you sure you want to log out?")) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setIsLoggedIn(false);
      setUserData({});
      setUserMenuOpen(false);

      // Dispatch custom event to notify other components about logout
      window.dispatchEvent(new Event("userLoggedOut"));

      // Redirect to home page
      navigate("/");
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  // Get user's first name or username
  const getDisplayName = () => {
    if (!userData) return "User";
    return userData.name?.split(" ")[0] || userData.username || "User";
  };

  // Check if the current route matches the given path
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 px-4 sm:px-8 md:px-16 bg-red-500
        ${
          scrolled
            ? "bg-gradient-to-r from-red-950 to-red-900 text-white shadow-lg py-2"
            : "bg-gradient-to-r from-red-950 to-red-800 text-white py-3"
        }`}
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-1">
            <Link to="/" className="flex items-center group">
              <div className="w-[76px] h-auto mr-2 overflow-hidden rounded border-2 border-amber-500 transition-all duration-300 group-hover:border-amber-300 group-hover:shadow-lg">
                <img
                  src="https://ik.imagekit.io/devhm/WhatsApp%20Image%202025-06-04%20at%2012.29.09.jpeg?updatedAt=1749036856478"
                  alt="Grand Cinemaz Logo"
                  className="w-full h-full object-cover"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/movies"
              className={`px-3 py-4 flex items-center font-medium hover:text-amber-300 transition-colors
                ${
                  isActive("/movies")
                    ? "text-amber-300 border-b-2 border-amber-400"
                    : ""
                }`}
            >
              <Film size={16} className="mr-1" /> Movies
            </Link>
            <Link
              to="/shows"
              className={`px-3 py-2 flex items-center font-medium hover:text-amber-300 transition-colors
                ${
                  isActive("/shows")
                    ? "text-amber-300 border-b-2 border-amber-400"
                    : ""
                }`}
            >
              <Calendar size={16} className="mr-1" /> Shows
            </Link>
            <Link
              to="/food"
              className={`px-3 py-2 flex items-center font-medium hover:text-amber-300 transition-colors
                ${
                  isActive("/food")
                    ? "text-amber-300 border-b-2 border-amber-400"
                    : ""
                }`}
            >
              <Popcorn size={16} className="mr-1" /> Food
            </Link>
            <Link
              to="/offers"
              className={`px-3 py-2 flex items-center font-medium hover:text-amber-300 transition-colors
                ${
                  isActive("/offers")
                    ? "text-amber-300 border-b-2 border-amber-400"
                    : ""
                }`}
            >
              <Gift size={16} className="mr-1" /> Offers
            </Link>
            <Link
              to="/blog"
              className={`px-3 py-2 flex items-center font-medium hover:text-amber-300 transition-colors
                ${
                  isActive("/blog")
                    ? "text-amber-300 border-b-2 border-amber-400"
                    : ""
                }`}
            >
              <Newspaper size={16} className="mr-1" /> Blogs
            </Link>

            {/* Login Button or User Menu */}
            {isLoggedIn ? (
              <div className="relative ml-4" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 transition-all duration-300 shadow-md hover:shadow-lg"
                  aria-expanded={userMenuOpen}
                  aria-label="User menu"
                >
                  <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-red-900">
                    <User size={16} />
                  </div>
                  <span className="font-medium">{getDisplayName()}</span>
                  <ChevronsDown
                    size={16}
                    className={`transition-transform duration-300 ${
                      userMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {userMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-64 rounded-lg shadow-xl bg-white text-gray-800 z-[999] pointer-events-auto border border-amber-200 overflow-hidden animate-fadeIn"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    <div className="px-4 py-3 bg-gradient-to-r from-red-100 to-red-50 border-b border-amber-200">
                      <p className="text-sm font-medium text-red-900 truncate">
                        {userData.name || "User"}
                      </p>
                      <p className="text-xs text-red-700 truncate">
                        {userData.email || ""}
                      </p>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center w-full text-left px-4 py-2 hover:bg-red-50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                        role="menuitem"
                      >
                        <User size={16} className="mr-2 text-red-700" /> Profile
                      </Link>
                      <Link
                        to="/bookings"
                        className="flex items-center w-full text-left px-4 py-2 hover:bg-red-50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                        role="menuitem"
                      >
                        <Ticket size={16} className="mr-2 text-red-700" /> My
                        Bookings
                      </Link>
                      <div className="border-t border-amber-200 my-1"></div>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleLogout();
                        }}
                        className="flex items-center w-full text-left px-4 py-2 hover:bg-red-50 transition-colors text-red-700"
                        role="menuitem"
                      >
                        <LogOut size={16} className="mr-2" /> Logout
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="ml-4 flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-red-950 font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                aria-label="Login"
              >
                <LogIn size={16} /> <span>Login</span>
              </button>
            )}
          </div>

          {/* Mobile User Menu and Toggle */}
          <div className="md:hidden flex items-center space-x-2">
            {isLoggedIn ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  className="p-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-red-950 transition-all duration-300 shadow-md"
                  aria-expanded={userMenuOpen}
                  aria-label="User menu"
                >
                  <User size={20} />
                </button>
                {userMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-56 rounded-lg shadow-xl bg-white text-gray-800 z-50 border border-amber-200 overflow-hidden animate-fadeIn"
                    role="menu"
                    aria-orientation="vertical"
                  >
                    <div className="px-4 py-3 bg-gradient-to-r from-red-100 to-red-50 border-b border-amber-200">
                      <p className="text-sm font-medium text-red-900 truncate">
                        {userData.name || "User"}
                      </p>
                      <p className="text-xs text-red-700 truncate">
                        {userData.email || ""}
                      </p>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center w-full text-left px-4 py-2 hover:bg-red-50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                        role="menuitem"
                      >
                        <User size={16} className="mr-2 text-red-700" /> Profile
                      </Link>
                      <Link
                        to="/bookings"
                        className="flex items-center w-full text-left px-4 py-2 hover:bg-red-50 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                        role="menuitem"
                      >
                        <Ticket size={16} className="mr-2 text-red-700" /> My
                        Bookings
                      </Link>
                      <div className="border-t border-amber-200 my-1"></div>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleLogout();
                        }}
                        className="flex items-center w-full text-left px-4 py-2 hover:bg-red-50 transition-colors text-red-700"
                        role="menuitem"
                      >
                        <LogOut size={16} className="mr-2" /> Logout
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="flex items-center space-x-1 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-red-950 font-medium text-sm transition-all duration-300 shadow-md"
                aria-label="Login"
              >
                <LogIn size={14} /> <span>Login</span>
              </button>
            )}
            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-md hover:bg-red-800 focus:outline-none transition-colors"
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="md:hidden bg-gradient-to-b from-red-900 to-red-950 border-t border-red-800 animate-fadeIn shadow-lg"
          ref={mobileMenuRef}
          role="menu"
        >
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/movies"
              className={`flex items-center px-3 py-3 rounded-md hover:bg-red-800 hover:text-amber-300 transition-colors
                ${isActive("/movies") ? "bg-red-800 text-amber-300" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
              role="menuitem"
            >
              <Film size={18} className="mr-2" /> Movies
            </Link>
            <Link
              to="/shows"
              className={`flex items-center px-3 py-3 rounded-md hover:bg-red-800 hover:text-amber-300 transition-colors
                ${isActive("/shows") ? "bg-red-800 text-amber-300" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
              role="menuitem"
            >
              <Calendar size={18} className="mr-2" /> Shows
            </Link>
            <Link
              to="/offers"
              className={`flex items-center px-3 py-3 rounded-md hover:bg-red-800 hover:text-amber-300 transition-colors
                ${isActive("/offers") ? "bg-red-800 text-amber-300" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
              role="menuitem"
            >
              <Gift size={18} className="mr-2" /> Offers
            </Link>
            {isLoggedIn && (
              <>
                <div className="border-t border-red-800 my-2"></div>
                <Link
                  to="/profile"
                  className={`flex items-center w-full text-left px-3 py-3 rounded-md hover:bg-red-800 transition-colors
                    ${isActive("/profile") ? "bg-red-800" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                  role="menuitem"
                >
                  <User size={18} className="mr-2" /> Profile
                </Link>
                <Link
                  to="/bookings"
                  className={`flex items-center w-full px-3 py-3 rounded-md hover:bg-red-800 transition-colors
                    ${isActive("/bookings") ? "bg-red-800" : ""}`}
                  onClick={() => setMobileMenuOpen(false)}
                  role="menuitem"
                >
                  <Ticket size={18} className="mr-2" /> My Bookings
                </Link>
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center w-full text-left px-3 py-3 rounded-md hover:bg-red-800 text-amber-300 transition-colors"
                  role="menuitem"
                >
                  <LogOut size={18} className="mr-2" /> Logout
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default PremiumNavbar;
